import ko from 'knockout';
import crossroads from 'crossroads';
import hasher from 'hasher';
import routes from '../routes';
import resourceHelper from '../Utils/resourceHelper';

/**
 * The router for the SPA that reacts to URL hash changes and causes a new page component load
 * @param {Object} routes
 */
function Router(routes) {

  const self = this;

  // When the SPA initally loads, if there are "resolve" promises for the route, set the intial page to
  // "loading-data" to display an animation to the user until the promises are resolved.
  self.currentRoute = ko.observable({ page: 'loading-data' });

  // The page title on the <title> element under the <head> element
  self.pageTitle = ko.observable();

  self.disablePageComponent = ko.observable(false);

  self.init = function () {

    ko.utils.arrayForEach(routes, function (route) {
      if (typeof route.isAvailable === 'function' && !route.isAvailable()) {
        return;
      }
      crossroads.addRoute(route.url, function (requestParams) {
        handleRouteChange(route, requestParams);
      });
    });

    crossroads.normalizeFn = crossroads.NORM_AS_OBJECT;

    // Execute an action every time crossroads can't match any route
    crossroads.bypassed.add(function (hash) {
      window.location.hash = '';
    });

    hasher.initialized.add(parseHash);
    hasher.changed.add(parseHash);
    hasher.init();
  };

  function handleRouteChange(route, requestParams) {

    const previousRoute = {
      page: self.currentRoute().page,
      id: self.currentRoute().id || null
    };

    const ariaLiveNavigationText = resourceHelper.getString('NavigatingToPage') + ' ' +
      resourceHelper.getString(route.params.pageTitleKey);

    let viewModelParams = ko.utils.extend(requestParams, route.params);
    viewModelParams = ko.utils.extend(viewModelParams, {
      previousRoute: previousRoute,
      ariaLiveNavigationText: ariaLiveNavigationText
    });

    const promisesToResolve = route.resolve;

    if (viewModelParams.vuePage) {
      // If a vue component page should be displayed then render vuePageWrapper.
      setVuePageWrapperComponent(viewModelParams);
    }

    if (!promisesToResolve) {
      self.currentRoute(viewModelParams);
      changePageTitle(requestParams.pageTitleKey);
      return;
    }

    self.disablePageComponent(true);

    getResolvedData(route.resolve, viewModelParams)
        .then(function (resolvedData) {

          viewModelParams = ko.utils.extend(viewModelParams, resolvedData);

          self.currentRoute(viewModelParams);
          changePageTitle(requestParams.pageTitleKey);
          self.disablePageComponent(false);

        })
        .catch(function (errorObject) {

          self.disablePageComponent(false);

          if (!errorObject.errorHasBeenLogged) {
            self.currentRoute({ page: 'page-not-available' });
          }
        });
  }

  /**
   * For each resolve property, execute the functions to return the promises and once each promise is
   * resolved, return an object with each of the resolve property names and the resolved promise values.
   * @param {*} resolve
   * @param {*} params
   * @return {*}
   */
  function getResolvedData(resolve, params) {

    const promises = [];
    const propertyNames = [];
    let promise;

    ko.utils.objectForEach(resolve, function (property) {
      promise = resolve[property](params);
      promises.push(promise);
      propertyNames.push(property);
    });

    return new Promise(function (resolve, reject) {

      Promise.all(promises)
          .then(function (resolvedItems) {

            let i;
            const resolvedData = {};

            for (i = 0; i <= promises.length; i++) {
              resolvedData[propertyNames[i]] = resolvedItems[i];
            }

            resolve(resolvedData);

          })
          .catch(function (e) {
            reject(e);
          });
    });
  }

  /**
   * Change the title on the <title> element under the <head> element
   * @param {string} pageTitleKey The key to use to lookup the translated text
   */
  function changePageTitle(pageTitleKey) {

    let title = resourceHelper.getString('PortalSpaWebpageTitle');

    if (pageTitleKey) {
      title = resourceHelper.getString(pageTitleKey) + ' | ' + title;
    }

    self.pageTitle(title);
  }

  function createVueAppWithoutAttributeInheritance(app) {
    // Clone the vue component object in order not to change the initial imported component
    // that can be used in other parts of the application
    return { ...app, inheritAttrs: false };
  }

  function setVuePageWrapperComponent(viewModelParams) {
    // Disable attribute fallthrough mechanism for the page component
    viewModelParams.vuePage = createVueAppWithoutAttributeInheritance(viewModelParams.vuePage);
    viewModelParams.pageComponent = 'vue-page-wrapper';
  }

  function parseHash(newHash) {
    crossroads.parse(newHash);
  }
}

export default new Router(routes);
